import {
  IOnlineBookingRequestPayload,
  IOnlineBookingService,
} from "services/online-booking/OnlineBookingService.types";
import { Cradle } from "services/serviceContainer.types";
import { IOnlineBookingEntity, OnlineBookingStatusUtil } from "models/OnlineBooking.model";
import { ServiceError, ServiceErrorCode } from "services/ServiceError";
import get from "lodash/get";
import toString from "lodash/toString";
import toNumber from "lodash/toNumber";

export class OnlineBookingService implements IOnlineBookingService {
  private readonly apiClient: Cradle["apiClient"];

  constructor(args: Pick<Cradle, "apiClient">) {
    this.apiClient = args.apiClient;
  }

  async requestBooking(args: {
    payload: IOnlineBookingRequestPayload;
    applicationId: number;
  }): Promise<IOnlineBookingEntity> {
    try {
      const response = await this.apiClient.protectedApi.post(
        `/user/applications/${args.applicationId}/inspection-bookings`,
        {
          ...args.payload,
        }
      );
      const json = response.data;
      const onlineBooking = this.parseOnlineBooking(json, args.applicationId);
      return onlineBooking;
    } catch (e) {
      throw ServiceError.createFromResponseError(e);
    }
  }

  async fetchOnlineBookings(args: { applicationId: number }): Promise<IOnlineBookingEntity[]> {
    try {
      const response = await this.apiClient.protectedApi.get(
        `/user/applications/${args.applicationId}/inspection-bookings`
      );
      const jsonArr = response.data;
      if (!Array.isArray(jsonArr)) {
        throw new ServiceError(ServiceErrorCode.ServerError, `Failed to fetch online bookings`);
      }
      return this.parseOnlineBookings(jsonArr, args.applicationId);
    } catch (e) {
      throw ServiceError.createFromResponseError(e);
    }
  }

  async fetchAvailableBookingTimes(args: { applicationId: number; inspectionType: string }): Promise<string[]> {
    try {
      const queryParams = new URLSearchParams();
      queryParams.set("inspection-type", args.inspectionType);
      const response = await this.apiClient.protectedApi.get(
        `/user/applications/${args.applicationId}/inspection-bookings/available?${queryParams.toString()}`
      );
      const jsonArr = response.data;
      if (!Array.isArray(jsonArr)) {
        throw new ServiceError(ServiceErrorCode.ServerError, `Failed to fetch online booking available times`);
      }
      return jsonArr;
    } catch (e) {
      throw ServiceError.createFromResponseError(e);
    }
  }

  private parseOnlineBookings(jsonArr: any[], applicationId: number) {
    const bookings: IOnlineBookingEntity[] = [];
    for (const json of jsonArr) {
      bookings.push(this.parseOnlineBooking(json, applicationId));
    }
    return bookings;
  }

  private parseOnlineBooking(json: string, applicationId: number): IOnlineBookingEntity {
    return {
      applicationId,
      id: toNumber(get(json, "id")),
      inspectionType: toString(get(json, "inspectionType")),
      location: toString(get(json, "location")),
      siteContactId: toNumber(get(json, "siteContact")),
      notes: toString(get(json, "notes")),
      bookingTime: toString(get(json, "bookingTime")),
      status: OnlineBookingStatusUtil.parse(toString(get(json, "status"))),
    };
  }
}
